import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useJourney } from '../JourneyContext';
import { Modal, Button } from '../../ui';
import { format } from 'date-fns';

const DraftManager = ({ isOpen, onClose, onSelectDraft, onStartFresh }) => {
  const { drafts, deleteDraft } = useJourney();

  const handleDelete = (e, draftId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this draft?')) {
      deleteDraft(draftId);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return 'Unknown date';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Continue Your Work"
      size="md"
    >
      <div className="space-y-4">
        <p className="text-gray-600 dark:text-gray-400">
          You have saved drafts. Would you like to continue where you left off?
        </p>

        {/* Drafts list */}
        <div className="space-y-2 max-h-80 overflow-y-auto">
          <AnimatePresence>
            {drafts.map((draft) => (
              <motion.button
                key={draft.draftId}
                type="button"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={() => onSelectDraft(draft.draftId)}
                className="w-full p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 text-left transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                      {draft.draftName || draft.company?.name || 'Untitled Draft'}
                    </h4>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatDate(draft.updatedAt)}
                      </span>
                      {draft.branches?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          {draft.branches.length} branch{draft.branches.length !== 1 ? 'es' : ''}
                        </span>
                      )}
                    </div>
                    
                    {/* Progress indicator */}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${((draft.currentStep + 1) / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400">
                        Step {draft.currentStep + 1}/5
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="ml-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, draft.draftId)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete draft"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>

        {drafts.length === 0 && (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">No saved drafts</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onStartFresh}>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Start Fresh
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DraftManager;

